import { computed, type ComputedRef, watch } from 'vue';
import {
  serializeCategories,
  serializeBrands,
  serializeCategoryActions,
} from '../sidebar.serializer';
import { storeToRefs } from 'pinia';
import { type UserAdditionalLink, useUserStore } from '~/domains/user';
import {
  type SidebarItem,
  ORDERS_OPEN_PATH,
  ORDERS_HISTORY_PATH,
  USER_OFFER_TERMS_TYPE,
  type SidebarActionItem,
} from '~/app/layouts/sidebar';
import { useI18n, usePermissions } from '~/infrastructure';
import { useCategoriesTree } from '~/domains/category';
import useOrderStore from '~/domains/order/stores/useOrderStore.ts';

interface ComposableReturn {
  items: ComputedRef<SidebarItem[]>;
  additionalItems: ComputedRef<SidebarItem[]>;
  additionalBottomLinks: ComputedRef<SidebarItem[]>;
}

export default function useSidebarItems(): ComposableReturn {
  const { categoriesTree, fetchCategoriesTree, macCTOProductTypes } =
    useCategoriesTree();
  const { isEntityHaveAccess } = usePermissions();
  const { user } = storeToRefs(useUserStore());
  const { t } = useI18n();
  const { resetViewedOrder } = useOrderStore();

  const categories = computed(() =>
    serializeCategories(categoriesTree.value?.tree.children),
  );

  const actions: ComputedRef<SidebarActionItem[] | SidebarItem[]> = computed(
    () => {
      const serializedActions = serializeCategoryActions(
        categoriesTree.value?.actions,
      );

      return serializedActions?.map((action) => ({
        ...action,
        label: t(`action.${action.type}.name`),
      }));
    },
  );

  const brands = computed(() => serializeBrands(categoriesTree.value?.brands));
  const ordersActiveCount = computed(
    () =>
      user.value?.ordersOverview?.orders?.find(
        (order) => order.status === 'ACTIVE',
      )?.qty,
  );
  const ordersHistoryCount = computed(
    () =>
      user.value?.ordersOverview?.orders?.find(
        (order) => order.status === 'HISTORY',
      )?.qty,
  );

  const macCTOLink = computed(() => {
    const firstProductType = macCTOProductTypes.value?.[0];

    return firstProductType !== undefined
      ? `/cto-apple/${firstProductType?.slug}`
      : null;
  });

  const items: ComputedRef<SidebarItem[]> = computed(() => [
    {
      id: 1,
      label: 'common.actions.titles.ROOT.text',
      icon: '$offers',
      searchInput: true,
      color: 'text-warning',
      isVisible: actions.value?.length > 0,
      children: actions.value,
    },
    {
      id: 2,
      label: 'page.catalog.title',
      icon: '$catalog',
      searchInput: true,
      isVisible: true,
      children: categories.value,
    },
    {
      id: 3,
      label: 'components.serviceCenters.title.tables.Brands',
      icon: '$shop',
      searchInput: true,
      sortByDesc: true,
      isVisible: true,
      children: brands.value,
    },
    {
      id: 4,
      label: 'header.menu.item.macCTO.text',
      icon: '$laptop',
      link: macCTOLink.value,
      isVisible: macCTOLink.value !== null,
    },
    {
      id: 14,
      label: 'header.menu.item.openOrders.text',
      icon: '$orderOpen',
      link: ORDERS_OPEN_PATH,
      isVisible: isEntityHaveAccess('order'),
      badge: ordersActiveCount.value,
      callback: resetViewedOrder,
    },
    {
      id: 15,
      label: 'header.menu.item.ordersHistory.text',
      icon: '$order',
      link: ORDERS_HISTORY_PATH,
      isVisible: isEntityHaveAccess('order'),
      badge: ordersHistoryCount.value,
      callback: resetViewedOrder,
    },
    {
      id: 16,
      label: 'header.menu.item.account.text',
      icon: '$coins',
      link: '/account',
      isVisible: isEntityHaveAccess('account'),
    },
    {
      id: 19,
      label: 'header.menu.item.dep.text',
      icon: '$dep',
      link: '/dep/enrollments',
      isVisible: isEntityHaveAccess('dep'),
    },
    {
      id: 17,
      label: 'page.warranty.title',
      icon: '$warranty',
      link: '/warranty/processing',
      isVisible: isEntityHaveAccess('warranty'),
    },
    {
      id: 18,
      label: 'Favourite',
      icon: '$starOutline',
      disabled: true,
      isVisible: false,
    },
    {
      id: 20,
      label: 'Trade desk',
      icon: '$tradeDesk',
      link: '/auction',
      isVisible: import.meta.env.VITE_USE_TRADE_DESK === 'true',
    },
  ]);

  const userOfferTerms: ComputedRef<UserAdditionalLink | undefined> = computed(
    () =>
      user.value?.additionalLinks?.find(
        (link: UserAdditionalLink) => link.type === USER_OFFER_TERMS_TYPE,
      ),
  );

  const additionalItems: ComputedRef<SidebarItem[]> = computed(() => [
    {
      id: 21,
      label: 'common.userOfferTerms.title',
      icon: '$fileIcon',
      link: userOfferTerms.value?.link,
      isVisible: userOfferTerms.value?.link !== undefined,
    },
    {
      id: 22,
      label: 'common.menu.item.xml.text',
      icon: '$xmlIcon',
      shortLabel: 'common.menu.item.xml.short.text',
      link: '/xml-exchange',
      isVisible: true,
    },
  ]);

  const additionalBottomLinks: ComputedRef<SidebarItem[]> = computed(() => [
    {
      id: 2,
      label: 'common.menu.item.orderingApi.text',
      shortLabel: 'common.menu.item.orderingApi.short.text',
      link: '/ordering-api',
      isVisible: false,
    },
  ]);

  watch(user, (newUser, prevUser) => {
    if (newUser?.selected.language === undefined) return;

    const isNewLang = newUser.selected.language !== prevUser?.selected.language;

    if (isNewLang) fetchCategoriesTree();
  });

  return {
    items,
    additionalItems,
    additionalBottomLinks,
  };
}
